import { logger } from '../lib/logger';
import { getPlatformInfo } from '../utils/platformUtils';
import { getRemoteConfig, type RemoteConfigResult, type IOSVersionConfigResult, type UnifiedVersionConfigResult } from '../lib/analytics';
import { getCurrentRegion } from '../atoms/shared/regionAtom';
import '../types/electron';

/**
 * 版本信息接口
 */
export interface VersionInfo {
  version: string;
  releaseNotes: string;
  downloadUrl: string;
  publishedAt: string;
  updateAction: 'download' | 'refresh' | 'testflight' | 'electron';
}

/**
 * 版本比较结果
 * -1: current < latest (需要更新)
 *  0: current = latest (无需更新)
 *  1: current > latest (当前版本更新)
 */
export type VersionCompareResult = -1 | 0 | 1;

/**
 * 更新检测服务
 */
export class UpdateService {
  private static readonly GITHUB_API_BASE = 'https://api.github.com';
  private static readonly REPO_OWNER = 'DramaVerse';
  private static readonly REPO_NAME = 'TempoTodo-Releases';
  private static readonly GITHUB_PROXY_BASE_URL = 'https://ghfast.top';

  /**
   * 默认 TestFlight URL（降级方案）
   */
  private static readonly DEFAULT_TESTFLIGHT_URL = 'https://testflight.apple.com/join/WAyZmxAV';

  /**
   * TestFlight URL 格式验证正则表达式
   */
  private static readonly TESTFLIGHT_URL_REGEX = /^https:\/\/testflight\.apple\.com\/join\/[A-Za-z0-9]+$/;

  /**
   * 获取当前应用版本号
   * 从构建配置中读取版本号
   */
  static getCurrentVersion(): string {
    // 从环境变量或构建时注入的版本号获取
    // 这里先返回一个默认值，后续会从实际构建配置读取
    return import.meta.env.VITE_APP_VERSION || '0.1.6';
  }

  /**
   * 获取 TestFlight 下载 URL
   * 优先从 PostHog 远程配置获取，失败时使用默认 URL
   */
  private static async getTestFlightUrl(): Promise<string> {
    try {
      // 尝试从 PostHog 获取远程配置
      const remoteConfig = await getRemoteConfig<RemoteConfigResult>('testflight_download_url');

      if (remoteConfig && remoteConfig.url) {
        // 验证 URL 格式
        if (this.TESTFLIGHT_URL_REGEX.test(remoteConfig.url)) {
          logger.info('使用 PostHog 远程配置的 TestFlight URL', { url: remoteConfig.url });
          return remoteConfig.url;
        } else {
          logger.warn('PostHog 远程配置的 TestFlight URL 格式无效', { url: remoteConfig.url });
        }
      }
    } catch (error) {
      logger.error('获取 PostHog 远程配置失败', error instanceof Error ? error : new Error(String(error)));
    }

    // 使用环境变量降级方案或默认 URL
    const fallbackUrl = import.meta.env.VITE_TESTFLIGHT_FALLBACK_URL || this.DEFAULT_TESTFLIGHT_URL;
    logger.info('使用降级方案的 TestFlight URL', { url: fallbackUrl });
    return fallbackUrl;
  }

  /**
   * 获取最新版本信息（根据平台选择不同策略）
   */
  static async getLatestVersion(): Promise<VersionInfo | null> {
    const platformInfo = getPlatformInfo();

    logger.debug('检查最新版本', { platform: platformInfo });

    // iOS 平台使用独立的处理逻辑
    if (platformInfo.isIOS && platformInfo.isNative) {
      return this.getIOSVersionInfo();
    }

    // 非 iOS 平台使用统一的处理逻辑
    return this.getNonIOSVersionInfo();
  }

  /**
   * iOS 平台独立版本检测（只使用 PostHog，不调用 GitHub API）
   */
  private static async getIOSVersionInfo(): Promise<VersionInfo | null> {
    try {
      // 获取 TestFlight 下载链接
      const downloadUrl = await this.getTestFlightUrl();

      // 获取 iOS 版本号
      const iosVersionConfig = await getRemoteConfig<IOSVersionConfigResult>('ios_testflight_version');

      if (!iosVersionConfig || !iosVersionConfig.version) {
        logger.info('iOS TestFlight 版本配置为空，跳过更新检测');
        return null;
      }

      const versionInfo: VersionInfo = {
        version: iosVersionConfig.version,
        releaseNotes: '版本更新',
        downloadUrl,
        publishedAt: new Date().toISOString(),
        updateAction: 'testflight'
      };

      logger.info('获取到 iOS 版本信息', {
        version: versionInfo.version,
        downloadUrl: versionInfo.downloadUrl,
        updateAction: versionInfo.updateAction
      });

      return versionInfo;
    } catch (error) {
      logger.error('获取 iOS 版本信息失败', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  /**
   * 非 iOS 平台统一版本检测（优先 PostHog，fallback 到 GitHub API）
   */
  private static async getNonIOSVersionInfo(): Promise<VersionInfo | null> {
    const platformInfo = getPlatformInfo();

    // 先尝试从 PostHog 获取统一版本号
    try {
      const unifiedVersionConfig = await getRemoteConfig<UnifiedVersionConfigResult>('unified_app_version');

      if (unifiedVersionConfig && unifiedVersionConfig.version) {
        logger.info('使用 PostHog 统一版本号', { version: unifiedVersionConfig.version });

        // 使用统一版本号，但仍需要 GitHub API 获取下载链接和发布信息
        return this.getVersionInfoFromGitHub(unifiedVersionConfig.version);
      }
    } catch (error) {
      logger.warn('获取 PostHog 统一版本号失败，fallback 到 GitHub API', error instanceof Error ? error : new Error(String(error)));
    }

    // Fallback 到 GitHub API
    return this.getVersionInfoFromGitHub();
  }

  /**
   * 从 GitHub API 获取版本信息
   */
  private static async getVersionInfoFromGitHub(overrideVersion?: string): Promise<VersionInfo | null> {
    try {
      const url = `${this.GITHUB_API_BASE}/repos/${this.REPO_OWNER}/${this.REPO_NAME}/releases/latest`;
      const platformInfo = getPlatformInfo();

      logger.debug('从 GitHub API 获取版本信息', { url, platform: platformInfo, overrideVersion });

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/vnd.github.v3+json',
          'User-Agent': 'TempoTodo-UpdateChecker'
        }
      });

      if (!response.ok) {
        throw new Error(`GitHub API 请求失败: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // 使用覆盖版本号或解析 GitHub 版本号
      const version = overrideVersion || data.tag_name?.replace(/^v/, '') || '';

      // 根据平台确定更新方式和链接
      let downloadUrl: string;
      let updateAction: 'download' | 'refresh' | 'testflight' | 'electron';

      if (platformInfo.isAndroid && platformInfo.isNative) {
        // Android 原生应用 - 下载 APK
        const androidAsset = data.assets?.find((asset: any) =>
          asset.name.includes('android') && asset.name.endsWith('.apk')
        );

        if (!androidAsset) {
          throw new Error('未找到 Android APK 文件');
        }

        downloadUrl = this.getProxiedDownloadUrl(androidAsset.browser_download_url);
        updateAction = 'download';

      } else if (platformInfo.isElectron) {
        // Electron 平台 - 根据操作系统选择合适的下载文件
        const os = platformInfo.os;
        let electronAsset = null;

        if (os === 'windows') {
          // Windows: 查找 .exe 安装包
          electronAsset = data.assets?.find((asset: any) =>
            asset.name.includes('windows') && asset.name.endsWith('.exe') && !asset.name.includes('portable')
          );
        } else if (os === 'macos') {
          // macOS: 查找 .dmg 文件
          electronAsset = data.assets?.find((asset: any) =>
            asset.name.includes('macos') && asset.name.endsWith('.dmg')
          );
        } else if (os === 'linux') {
          // Linux: 查找 .AppImage 文件
          electronAsset = data.assets?.find((asset: any) =>
            asset.name.includes('linux') && asset.name.endsWith('.AppImage')
          );
        }

        // 如果找到了对应平台的文件，使用直接下载链接；否则回退到 GitHub Release 页面
        downloadUrl = electronAsset ? this.getProxiedDownloadUrl(electronAsset.browser_download_url) : data.html_url;
        updateAction = electronAsset ? 'download' : 'electron';

      } else if (platformInfo.isPWA || !platformInfo.isNative) {
        // PWA 或 Web 应用 - 强制刷新
        downloadUrl = window.location.origin;
        updateAction = 'refresh';

      } else {
        // 其他平台 - 默认下载
        downloadUrl = data.html_url; // GitHub Release 页面
        updateAction = 'download';
      }

      // 解析更新说明
      const releaseNotes = this.parseReleaseNotes(data.body || '');

      const versionInfo: VersionInfo = {
        version,
        releaseNotes,
        downloadUrl,
        publishedAt: data.published_at,
        updateAction
      };

      logger.info('获取到最新版本信息', {
        version: versionInfo.version,
        downloadUrl: versionInfo.downloadUrl,
        updateAction: versionInfo.updateAction,
        platform: platformInfo,
        publishedAt: versionInfo.publishedAt
      });

      return versionInfo;

    } catch (error) {
      logger.error('获取最新版本失败', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  /**
   * 根据用户地区生成代理下载链接
   * 国内用户自动使用 ghfast.top 代理，海外用户使用原始链接
   */
  private static getProxiedDownloadUrl(originalUrl: string): string {
    try {
      const currentRegion = getCurrentRegion();

      // 如果是国内用户，使用 ghfast.top 代理
      if (currentRegion === 'cn') {
        const proxiedUrl = `${this.GITHUB_PROXY_BASE_URL}/${originalUrl}`;
        logger.info('使用国内代理下载链接', {
          originalUrl,
          proxiedUrl,
          region: currentRegion
        });
        return proxiedUrl;
      }

      // 海外用户使用原始链接
      logger.debug('使用原始下载链接', {
        originalUrl,
        region: currentRegion
      });
      return originalUrl;
    } catch (error) {
      logger.warn('生成代理下载链接失败，使用原始链接', error instanceof Error ? error : new Error(String(error)));
      return originalUrl;
    }
  }

  /**
   * 解析 GitHub Release body 中的更新说明
   * 提取主要的更新内容
   */
  private static parseReleaseNotes(body: string): string {
    if (!body) return '版本更新';

    // 尝试提取 "What's New" 或类似部分
    const lines = body.split('\n');
    const updateLines: string[] = [];
    let inUpdateSection = false;

    for (const line of lines) {
      const trimmed = line.trim();

      // 检测更新内容开始
      if (trimmed.match(/^#+\s*(✨|🆕|📝|What's New|更新内容|新功能)/i)) {
        inUpdateSection = true;
        continue;
      }

      // 检测下一个章节开始
      if (inUpdateSection && trimmed.match(/^#+\s*/)) {
        break;
      }

      // 收集更新内容
      if (inUpdateSection && trimmed) {
        // 清理 markdown 格式
        const cleaned = trimmed
          .replace(/^[-*+]\s*/, '• ')  // 转换列表符号
          .replace(/\*\*(.*?)\*\*/g, '$1')  // 移除粗体
          .replace(/\*(.*?)\*/g, '$1')      // 移除斜体
          .replace(/`(.*?)`/g, '$1');       // 移除代码格式

        if (cleaned.startsWith('•') || cleaned.match(/^\d+\./)) {
          updateLines.push(cleaned);
        }
      }
    }

    // 如果没有找到结构化的更新内容，返回简化版本
    if (updateLines.length === 0) {
      return '• 功能改进和错误修复\n• 性能优化和稳定性提升';
    }

    // 限制显示的条目数量
    return updateLines.slice(0, 5).join('\n');
  }

  /**
   * 比较两个版本号
   * 支持 semver 格式：major.minor.patch
   */
  static compareVersions(current: string, latest: string): VersionCompareResult {
    try {
      const currentParts = current.split('.').map(Number);
      const latestParts = latest.split('.').map(Number);

      // 确保版本号格式正确
      if (currentParts.some(isNaN) || latestParts.some(isNaN)) {
        logger.warn('版本号格式不正确', { current, latest });
        return 0;
      }

      // 补齐版本号位数
      while (currentParts.length < 3) currentParts.push(0);
      while (latestParts.length < 3) latestParts.push(0);

      // 逐位比较
      for (let i = 0; i < 3; i++) {
        if (currentParts[i] < latestParts[i]) return -1;
        if (currentParts[i] > latestParts[i]) return 1;
      }

      return 0;
    } catch (error) {
      logger.error('版本号比较失败', error instanceof Error ? error : new Error(String(error)));
      return 0;
    }
  }

  /**
   * Electron 原生更新检查
   * 通过 IPC 与主进程通信
   */
  static async checkElectronUpdates(): Promise<{
    hasUpdate: boolean;
    currentVersion: string;
    latestVersion?: VersionInfo;
  }> {
    try {
      // 检查是否在 Electron 环境中
      if (!window.electronAPI) {
        throw new Error('Electron API 不可用');
      }

      logger.info('开始 Electron 原生更新检查');

      // 调用主进程的更新检查
      const result = await window.electronAPI.checkForUpdates();

      logger.info('Electron 更新检查完成', result);

      return {
        hasUpdate: result.hasUpdate,
        currentVersion: result.currentVersion,
        latestVersion: result.hasUpdate ? {
          version: result.latestVersion || 'unknown',
          releaseNotes: result.releaseNotes || '• 功能改进和错误修复\n• 性能优化和稳定性提升',
          downloadUrl: '',
          publishedAt: new Date().toISOString(),
          updateAction: 'electron'
        } : undefined
      };

    } catch (error) {
      logger.error('Electron 更新检查失败', error instanceof Error ? error : new Error(String(error)));

      // 如果 Electron 原生更新失败，回退到 Web 方式
      logger.info('回退到 Web 更新检查方式');
      const fallbackResult = await this.checkForUpdates();

      // 确保回退时的 updateAction 是 download，这样用户可以通过浏览器下载
      if (fallbackResult.hasUpdate && fallbackResult.latestVersion) {
        fallbackResult.latestVersion.updateAction = 'download';
      }

      return fallbackResult;
    }
  }

  /**
   * 检查是否有可用更新
   */
  static async checkForUpdates(): Promise<{
    hasUpdate: boolean;
    currentVersion: string;
    latestVersion?: VersionInfo;
  }> {
    const currentVersion = this.getCurrentVersion();
    const latestVersion = await this.getLatestVersion();

    if (!latestVersion) {
      return {
        hasUpdate: false,
        currentVersion
      };
    }

    const compareResult = this.compareVersions(currentVersion, latestVersion.version);
    const hasUpdate = compareResult === -1;

    logger.info('更新检查完成', {
      currentVersion,
      latestVersion: latestVersion.version,
      hasUpdate,
      compareResult
    });

    return {
      hasUpdate,
      currentVersion,
      latestVersion: hasUpdate ? latestVersion : undefined
    };
  }
}
